import React from "react";
import { WAREHOUSE } from "./consumableHelpers";

// Adjusting the count of an untracked type, on the row it belongs to.
//
// This replaces the standalone "Adjust counted stock" panel, which asked you to pick
// the type from a dropdown even though you had just clicked that type in the list.
// Here the type is the row, so the form only asks the two things it cannot know:
// which location, and by how much.
//
// The change is a delta, not a total. Booking in 96 cables is "+96", not "set the
// total to 1,204": a delta records what happened and cannot silently discard a change
// someone else made to the same count.

const controlStyle = {
  height: 32,
  padding: "0 10px",
  borderRadius: "var(--radius-md, 6px)",
  outline: "none",
  border: "1px solid var(--border-default)",
  background: "var(--surface-default)",
  color: "var(--text-primary)",
  fontFamily: "inherit",
  fontSize: 13,
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--text-muted)",
};

// Common booking sizes, so the usual case is one click rather than typing.
const PRESETS = [-1, +1, +10, +50];

export default function InlineAdjust({ row, locations, quantities, onAdjust }) {
  const [locName, setLocName] = React.useState(WAREHOUSE);
  const [delta, setDelta] = React.useState("");
  const [note, setNote] = React.useState("");

  const locOptions = React.useMemo(
    () => [WAREHOUSE, ...locations.filter(l => l !== WAREHOUSE)],
    [locations]
  );

  const n = parseInt(delta, 10);
  const valid = Number.isFinite(n) && n !== 0;
  const current = ((quantities || {})[locName] || {})[row.id] || 0;
  // A negative adjustment cannot take a location below zero — there is no such thing
  // as holding minus four cables, and allowing it would quietly corrupt the totals.
  const wouldGoNegative = valid && n < 0 && current + n < 0;

  const submit = () => {
    if (!valid || wouldGoNegative) return;
    onAdjust({ typeId: row.id, locationName: locName, delta: n, note: note.trim() });
    setDelta("");
    setNote("");
  };

  const bump = amount => {
    const next = (Number.isFinite(n) ? n : 0) + amount;
    setDelta(next > 0 ? "+" + next : String(next));
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 12,
        borderRadius: "var(--radius-md, 6px)",
        background: "var(--surface-default)",
        border: "1px solid var(--border-subtle)",
      }}
      // The row header is a button; without this a click on a field would toggle
      // the row shut underneath the form.
      onClick={e => e.stopPropagation()}
    >
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 150px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={labelStyle} htmlFor={"adj-loc-" + row.id}>
            Location
          </label>
          <select
            id={"adj-loc-" + row.id}
            style={controlStyle}
            value={locName}
            onChange={e => setLocName(e.target.value)}
          >
            {locOptions.map(l => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>

        <div style={{ width: 110, display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={labelStyle} htmlFor={"adj-delta-" + row.id}>
            Change
          </label>
          <input
            id={"adj-delta-" + row.id}
            style={{ ...controlStyle, fontVariantNumeric: "tabular-nums" }}
            value={delta}
            onChange={e => setDelta(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            inputMode="numeric"
            placeholder="+96"
          />
        </div>

        <div style={{ display: "flex", gap: 4, paddingBottom: 1 }}>
          {PRESETS.map(p => (
            <button
              key={p}
              type="button"
              className="seg-btn"
              onClick={() => bump(p)}
              style={{ height: 32 }}
            >
              {p > 0 ? "+" + p : String(p)}
            </button>
          ))}
        </div>

        <div style={{ flex: "2 1 180px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={labelStyle} htmlFor={"adj-note-" + row.id}>
            Note
          </label>
          <input
            id={"adj-note-" + row.id}
            style={controlStyle}
            value={note}
            onChange={e => setNote(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Booked in · PO-12488"
          />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span
          style={{
            flex: 1,
            fontSize: 12,
            color: wouldGoNegative ? "var(--danger-text)" : "var(--text-muted)",
          }}
        >
          {wouldGoNegative
            ? locName + " holds " + current + ". That would take it below zero."
            : locName +
              " holds " +
              current.toLocaleString("en-GB") +
              (valid ? " → " + (current + n).toLocaleString("en-GB") : "") +
              "."}
        </span>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!valid || wouldGoNegative}
          onClick={submit}
        >
          Apply
        </button>
      </div>
    </div>
  );
}
